import { useState } from 'react';
import { RecipeService } from '@/lib/recipeService';
import { CollectionService } from '@/lib/collectionService';
import { toaster } from '@/lib/toaster';
import { apiRequest } from '@/lib/queryClient';
import { useChatContext } from '@/hooks/useChatContext';
import type {
  SendMessageRequest,
  SendMessageResponse,
  ConfirmActionRequest,
  ConfirmActionResponse,
} from '@/types/chat';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

interface UseChatMessagesOptions {
  recipeId?: string;
  collectionId?: string;
}

export function useChatMessages({ recipeId, collectionId }: UseChatMessagesOptions = {}) {
  const {
    conversationId,
    messages,
    pendingAction,
    setConversationId,
    setMessages,
    setPendingAction,
    resetChat,
  } = useChatContext();

  const [isSending, setIsSending] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const sendMessage = (message: string) => {
    if (!message.trim()) return;

    const userMessage = message.trim();

    // Dismiss any pending action card when the user sends a new message
    setPendingAction(null);

    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: userMessage,
        timestamp: new Date().toISOString(),
      },
    ]);

    const data: SendMessageRequest = {
      message: userMessage,
      conversationId: conversationId || undefined,
      currentRecipeId: recipeId,
      currentCollectionId: collectionId,
    };

    setIsSending(true);
    apiRequest('POST', '/chat', data)
      .then((response) => response.json() as Promise<SendMessageResponse>)
      .then((result) => {
        if (result.message) {
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', content: result.message, timestamp: result.timestamp },
          ]);
        }
        setConversationId(result.conversationId);
        setPendingAction(result.pendingAction ?? null);
      })
      .catch((error: Error) => {
        console.error('Chat message error:', error);
        toaster.create({
          title: 'Error',
          description: error?.message || 'Failed to send message',
          type: 'error',
        });
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  const confirmAction = () => {
    if (!pendingAction) return;
    const action = pendingAction;

    const body: ConfirmActionRequest = {
      conversationId: conversationId!,
      action,
    };

    setIsConfirming(true);
    apiRequest('POST', '/chat/actions/confirm', body)
      .then((response) => response.json() as Promise<ConfirmActionResponse>)
      .then((data) => {
        setPendingAction(null);

        if (data.kind === 'recipe') {
          void RecipeService.loadOne(data.recipeId);
          const verb = action.type === 'create' ? 'Saved' : 'Updated';
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant' as const,
              content: `${verb}! You can view it here: [${data.recipeTitle}](recipe://${data.recipeId})`,
              timestamp: new Date().toISOString(),
            },
          ]);
          return;
        }

        // Collection actions — data.kind === 'collection'

        if (action.type === 'collection_create') {
          void CollectionService.loadOne(data.collectionId);
          const hasInitialRecipes = action.initialRecipeNames.length > 0;
          const recipeList = hasInitialRecipes
            ? `\n\nAdded: ${action.initialRecipeNames.join(', ')}. The shopping list will be generated.`
            : '';
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant' as const,
              content: `Done! View your new collection: [${data.collectionTitle}](collection://${data.collectionId})${recipeList}`,
              timestamp: new Date().toISOString(),
            },
          ]);
          return;
        }

        if (action.type === 'collection_add_recipes') {
          void CollectionService.loadOne(action.collectionId);
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant' as const,
              content: `Done! [${data.collectionTitle}](collection://${data.collectionId}) has been updated. The shopping list will be regenerated.`,
              timestamp: new Date().toISOString(),
            },
          ]);
          return;
        }

        if (action.type === 'collection_remove_recipe') {
          void CollectionService.loadOne(action.collectionId);
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant' as const,
              content: `Done! ${action.recipeName} removed from [${data.collectionTitle}](collection://${data.collectionId}). The shopping list will be regenerated.`,
              timestamp: new Date().toISOString(),
            },
          ]);
          return;
        }

        // collection_rename — update the cached collection in place
        void CollectionService.loadOne(data.collectionId);
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant' as const,
            content: `Done! Collection renamed. View it here: [${data.collectionTitle}](collection://${data.collectionId})`,
            timestamp: new Date().toISOString(),
          },
        ]);
      })
      .catch((error: Error) => {
        console.error('Confirm action error:', error);
        toaster.create({ title: 'Update failed', description: error?.message || 'Unknown error', type: 'error' });
        setPendingAction(null);
        const isCollectionAction = action.type.startsWith('collection_');
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant' as const,
            content: isCollectionAction
              ? "Sorry, that didn't work — please try again."
              : "Sorry, I couldn't save the recipe — please try again.",
            timestamp: new Date().toISOString(),
          },
        ]);
      })
      .finally(() => {
        setIsConfirming(false);
      });
  };

  const cancelAction = () => {
    setPendingAction(null);
    setMessages((prev) => [
      ...prev,
      {
        role: 'assistant' as const,
        content: "No problem, I won't save it.",
        timestamp: new Date().toISOString(),
      },
    ]);
  };

  return {
    messages,
    pendingAction,
    sendMessage,
    confirmAction,
    cancelAction,
    resetChat,
    isLoading: isSending,
    isConfirming,
  };
}
